/**
 * Select Mast Variant Picker - Pre-computed JSON
 *
 * Custom variant picker for mast products that navigates between separate products.
 * Each mast configuration is a separate product linked via metafields (not Shopify variants).
 *
 * Architecture:
 * - Diameter: Pills showing available diameters (RDM, SDM) with length counts
 * - Length: Grid of length buttons that filter available curves
 * - Curve: Pills showing available curves (Constant, FH, FL) for selected diameter+length
 * - Uses pre-computed variation_data JSON metafield (no all_products lookups)
 *
 * Expected JSON structure:
 * {
 *   "currentProduct": { "handle": "...", "diameter": "RDM", "length": 340, "curve": "constant", ... },
 *   "variationData": {
 *     "diameters": {
 *       "RDM": {
 *         "340": {
 *           "constant": { "handle": "...", "price": 41900, "available": true, "url": "...", "image": "..." },
 *           "fl": { ... },
 *           "fh": { ... }
 *         },
 *         "370": { ... }
 *       },
 *       "SDM": { ... }
 *     },
 *     "currentDiameter": "RDM",
 *     "currentLength": 340,
 *     "currentCurve": "constant"
 *   }
 * }
 */

class SelectMastVariantPicker extends HTMLElement {
  constructor() {
    super();
    this.data = null;
    this.variationData = null;
    this.currentDiameter = null;
    this.currentLength = null;
    this.currentCurve = null;
    this.includesMastbag = false;
    this.diameters = {};
  }

  connectedCallback() {
    // Parse JSON data
    const dataScript = this.querySelector("[data-mast-variants]");
    if (!dataScript) {
      console.warn("SelectMastVariantPicker: No data script found");
      return;
    }

    try {
      this.data = JSON.parse(dataScript.textContent);

      // Get variation data
      if (this.data.variationData) {
        this.variationData = this.data.variationData;
        this.diameters = this.variationData.diameters || {};
      }

      // Set current selections
      this.currentDiameter = this.data.currentProduct.diameter || this.variationData?.currentDiameter || null;
      this.currentLength = this.data.currentProduct.length || this.variationData?.currentLength || null;
      this.currentCurve = this.data.currentProduct.curve || this.variationData?.currentCurve || null;
      this.includesMastbag = Boolean(this.data.currentProduct.includesMastbag);

      // Initialize UI
      this.buildDiameterPills();
      this.buildLengthGrid();
      this.buildCurvePills();
      this.bindEvents();
      this.updateConfigurationSummary();
    } catch (error) {
      console.error("SelectMastVariantPicker: Error parsing data", error);
      console.error("Data script content:", dataScript.textContent);
    }
  }

  /**
   * Build diameter pills (RDM, SDM)
   */
  buildDiameterPills() {
    const container = this.querySelector("[data-diameter-pills]") || this.querySelector("#diameterPills");
    if (!container) return;

    container.innerHTML = "";

    const diameterKeys = Object.keys(this.diameters).sort();

    diameterKeys.forEach((diameter) => {
      const lengths = this.diameters[diameter];
      const lengthCount = Object.keys(lengths).length;
      const isActive = diameter === this.currentDiameter;

      const pill = document.createElement("button");
      pill.className = `diameter-pill ${isActive ? "active" : ""}`;
      pill.dataset.diameter = diameter;
      pill.type = "button";
      pill.setAttribute("aria-pressed", isActive ? "true" : "false");
      pill.type = "button";
      pill.innerHTML = `
        ${diameter}<span class="count">(${lengthCount})</span>
      `;

      container.appendChild(pill);
    });

    const selectedDiameterEl =
      this.querySelector("[data-selected-diameter]") || this.querySelector("#selectedDiameter");
    if (selectedDiameterEl) {
      selectedDiameterEl.textContent = this.currentDiameter || "Select diameter";
    }
  }

  /**
   * Build length grid for the current diameter
   */
  buildLengthGrid() {
    const container = this.querySelector("[data-length-grid]") || this.querySelector("#lengthGrid");
    if (!container) return;

    if (!this.currentDiameter || !this.diameters[this.currentDiameter]) {
      container.innerHTML =
        '<p style="color: var(--color-text-muted); font-size: 14px; font-style: italic;">Select a diameter first.</p>';
      const selectedLengthEl = this.querySelector("[data-selected-length]") || this.querySelector("#selectedLength");
      if (selectedLengthEl) {
        selectedLengthEl.textContent = "Select length";
      }
      return;
    }

    const lengths = this.diameters[this.currentDiameter];
    const lengthKeys = Object.keys(lengths)
      .map(Number)
      .sort((a, b) => a - b);

    container.innerHTML = "";

    if (lengthKeys.length === 0) {
      container.innerHTML =
        '<p style="color: var(--color-text-muted); font-size: 14px; font-style: italic;">No lengths available for this diameter.</p>';
      const selectedLengthEl = this.querySelector("[data-selected-length]") || this.querySelector("#selectedLength");
      if (selectedLengthEl) {
        selectedLengthEl.textContent = "Select length";
      }
      return;
    }

    lengthKeys.forEach((length) => {
      const curves = lengths[length];
      const isActive = length === this.currentLength;

      // Check if any curve is available
      const hasAvailable = Object.values(curves).some((curve) => curve.available !== false);

      const button = document.createElement("button");
      button.className = `length-btn ${isActive ? "active" : ""} ${!hasAvailable ? "out-of-stock" : ""}`;
      button.dataset.length = length;
      button.type = "button";
      button.textContent = length;
      button.disabled = !hasAvailable;
      button.setAttribute("aria-pressed", isActive ? "true" : "false");

      container.appendChild(button);
    });

    // Update selected length display
    const selectedLengthEl = this.querySelector("[data-selected-length]") || this.querySelector("#selectedLength");
    if (selectedLengthEl) {
      selectedLengthEl.textContent = this.currentLength ? `${this.currentLength} cm` : "Select length";
    }
  }

  /**
   * Build curve pills for the current diameter+length combination
   */
  buildCurvePills() {
    const container = this.querySelector("[data-curve-pills]") || this.querySelector("#curvePills");
    if (!container) return;

    if (!this.currentDiameter || !this.currentLength || !this.diameters[this.currentDiameter]) {
      container.innerHTML =
        '<p style="color: var(--color-text-muted); font-size: 14px; font-style: italic;">Select a diameter and length first.</p>';
      const selectedCurveEl = this.querySelector("[data-selected-curve]") || this.querySelector("#selectedCurve");
      if (selectedCurveEl) {
        selectedCurveEl.textContent = "Select curve";
      }
      return;
    }

    const curves = this.diameters[this.currentDiameter][this.currentLength];
    if (!curves) {
      container.innerHTML =
        '<p style="color: var(--color-text-muted); font-size: 14px; font-style: italic;">No curves available for this combination.</p>';
      const selectedCurveEl = this.querySelector("[data-selected-curve]") || this.querySelector("#selectedCurve");
      if (selectedCurveEl) {
        selectedCurveEl.textContent = "Select curve";
      }
      return;
    }

    container.innerHTML = "";

    // Curve order: constant, fh, fl
    const curveOrder = ["constant", "fh", "fl"];
    const curveLabels = {
      constant: "Constant Curve",
      fh: "FH Curve",
      fl: "FL Curve",
    };
    const curveBadges = {
      constant: "CC",
      fh: "FH",
      fl: "FL",
    };

    curveOrder.forEach((curve) => {
      if (!curves[curve]) return;

      const curveData = curves[curve];
      const isActive = curve === this.currentCurve;
      const isOutOfStock = !curveData.available;

      const pill = document.createElement("button");
      pill.className = `curve-pill ${isActive ? "active" : ""} ${isOutOfStock ? "out-of-stock" : ""}`;
      pill.dataset.curve = curve;
      pill.dataset.handle = curveData.handle || "";
      pill.dataset.url = curveData.url || "";
      pill.type = "button";
      pill.disabled = isOutOfStock;
      pill.setAttribute("aria-pressed", isActive ? "true" : "false");
      pill.innerHTML = `
        <span class="curve-badge curve-badge-${curve}">${curveBadges[curve]}</span>
        <span class="curve-label">${curveLabels[curve]}</span>
      `;

      if (!isOutOfStock && curveData.url) {
        pill.addEventListener("click", () => {
          window.location.href = curveData.url;
        });
      }

      container.appendChild(pill);
    });

    // Update selected curve display
    const selectedCurveEl = this.querySelector("[data-selected-curve]") || this.querySelector("#selectedCurve");
    if (selectedCurveEl) {
      const curveLabel = this.currentCurve ? curveLabels[this.currentCurve] || this.currentCurve : "Select curve";
      selectedCurveEl.textContent = curveLabel;
    }
  }

  /**
   * Return the storefront label for a mast curve key.
   */
  getCurveLabel(curve) {
    const curveLabels = {
      constant: "Constant Curve",
      fh: "FH Curve",
      fl: "FL Curve",
    };

    return curveLabels[curve] || curve || "Select curve";
  }

  /**
   * Return the selected curve data when a full configuration is selected.
   */
  getCurrentCurveData() {
    if (!this.currentDiameter || !this.currentLength || !this.currentCurve) {
      return null;
    }

    return this.diameters?.[this.currentDiameter]?.[this.currentLength]?.[this.currentCurve] || null;
  }

  /**
   * Detect whether the current or selected product is the complete mast package.
   */
  getSelectedIncludesMastbag() {
    const curveData = this.getCurrentCurveData();

    if (typeof curveData?.includesMastbag === "boolean") {
      return curveData.includesMastbag;
    }

    if (typeof curveData?.handle === "string") {
      return curveData.handle.includes("incl-mastbag");
    }

    return this.includesMastbag;
  }

  /**
   * Keep the configuration summary aligned with the current selections.
   */
  updateConfigurationSummary() {
    const summaryEl = this.querySelector("[data-summary-configuration]");
    const mastbagEl = this.querySelector("[data-summary-mastbag]");

    if (summaryEl) {
      const diameter = this.currentDiameter || "Select diameter";
      const length = this.currentLength ? `${this.currentLength} cm` : "Select length";
      const curve = this.currentCurve ? this.getCurveLabel(this.currentCurve) : "Select curve";
      summaryEl.textContent = `${diameter} / ${length} / ${curve}`;
    }

    if (mastbagEl) {
      mastbagEl.hidden = !this.getSelectedIncludesMastbag();
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // FOCUS RESTORE AFTER RE-RENDER
  // ═══════════════════════════════════════════════════════════════════════════════

  /**
   * Re-rendering a grid destroys the focused button. Move focus to the equivalent
   * pill in the freshly rendered DOM so keyboard users do not lose their place.
   */
  restoreFocusToPill(pillSelector) {
    const pill = this.querySelector(pillSelector);
    if (pill && !pill.disabled) {
      pill.focus({ preventScroll: true });
    }
  }

  /**
   * Handle diameter pill click - update length and curve grids
   */
  handleDiameterClick(diameter) {
    if (diameter === this.currentDiameter) return;

    this.currentDiameter = diameter;
    this.currentLength = null;
    this.currentCurve = null;

    this.buildDiameterPills();
    this.buildLengthGrid();
    this.buildCurvePills();
    this.updateConfigurationSummary();
    this.restoreFocusToPill(`[data-diameter="${diameter}"]`);
  }

  /**
   * Handle length button click - update curve pills
   */
  handleLengthClick(length) {
    if (length === this.currentLength) return;

    this.currentLength = length;
    this.currentCurve = null;

    this.buildLengthGrid();
    this.buildCurvePills();
    this.updateConfigurationSummary();
    this.restoreFocusToPill(`[data-length="${length}"]`);
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    const diameterContainer = this.querySelector("[data-diameter-pills]") || this.querySelector("#diameterPills");
    if (diameterContainer && !diameterContainer.dataset.mastPickerBound) {
      diameterContainer.dataset.mastPickerBound = "true";
      diameterContainer.addEventListener("click", (e) => {
        const pill = e.target.closest("button");
        if (!pill || !diameterContainer.contains(pill)) return;

        const diameter = pill.dataset.diameter;
        if (diameter) {
          this.handleDiameterClick(diameter);
        }
      });
    }

    const lengthContainer = this.querySelector("[data-length-grid]") || this.querySelector("#lengthGrid");
    if (lengthContainer && !lengthContainer.dataset.mastPickerBound) {
      lengthContainer.dataset.mastPickerBound = "true";
      lengthContainer.addEventListener("click", (e) => {
        const button = e.target.closest("button");
        if (!button || !lengthContainer.contains(button)) return;

        const length = parseInt(button.dataset.length);
        if (length && !button.disabled) {
          this.handleLengthClick(length);
        }
      });
    }

    // Curve pills - navigation is handled in buildCurvePills
  }

  /**
   * Navigate to a different product
   */
  navigateToProduct(handle) {
    if (!this.variationData || !this.variationData.diameters) return;

    // Find the product in variation data
    for (const diameter in this.variationData.diameters) {
      for (const length in this.variationData.diameters[diameter]) {
        for (const curve in this.variationData.diameters[diameter][length]) {
          const product = this.variationData.diameters[diameter][length][curve];
          if (product.handle === handle && product.url) {
            window.location.href = product.url;
            return;
          }
        }
      }
    }
  }
}

// Register custom element
if (!customElements.get("select-mast-variant-picker")) {
  customElements.define("select-mast-variant-picker", SelectMastVariantPicker);
}
